import { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import {
  MapContainer,
  LayersControl,
  TileLayer,
  FeatureGroup,
  GeoJSON,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { number } from "../../utils";
import { boundaryCRUD } from "../../actions/boundary";
import { buildingCRUD } from "../../actions/building";
import { kayaCRUD } from "../../actions/kaya";
import { Footer } from "../../layout/footer";
import { Link } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { boundaryListAction } from "../../actions/boundary/actions";
import { selector } from "../../api";

export const ShehaContent = () => {
  const { BaseLayer } = LayersControl;
  const dispatch = useDispatch();
  const mapRef = useRef();
  const stateBoundaries = useSelector((state) => state.boundaries);
  const user = selector.User();
  const [zoom, setZoom] = useState(0);
  const [shehia, setShehia] = useState([]);
  const [zone, setZone] = useState([]);
  const [building, setBuilding] = useState([]);
  const [kayaCount, setKayaCount] = useState(0);
  const [memberCount, setMemberCount] = useState(0);
  const [buildingRCount, setBuildingRCount] = useState(0);
  const [buildingCCount, setBuildingCCount] = useState(0);
  const [buildingCiCount, setBuildingCiCount] = useState(0);
  const [buildingReCount, setBuildingReCount] = useState(0);
  const [buildingMCount, setBuildingMCount] = useState(0);
  const [buildingTotalCount, setBuildingTotalCount] = useState(0);
  const [yAxisMale, setYAxisMale] = useState([]);
  const [yAxisFemale, setYAxisFemale] = useState([]);
  const [eduMale, setEduMale] = useState([]);
  const [eduFemale, setEduFemale] = useState([]);
  // Order MUST match the backend education report array order
  // (id55=Pre Primary, id56=Primary, id57=Secondary, id54=No Formal Education).
  const eduLabels = [
    "Pre Primary",
    "Primary",
    "Secondary",
    "No Formal Education",
  ];
  const [xAxis] = useState([
    "0 - 5",
    "6 - 11",
    "12 - 17",
    "18 - 25",
    "26 - 35",
    "36 - 45",
    "46 - 55",
    "56 - 65",
    "66 - 75",
    "76 - 85",
    "86 - Above",
  ]);

  useEffect(() => {
    if (stateBoundaries.boudaryList.length == 0) {
      boundaryCRUD.getBoundaries().then((resp) => {
        dispatch(boundaryListAction(resp.data));
      });
    }
  }, []);

  useEffect(() => {
    // Don't load data until user is available
    if (!user) return;

    setTimeout(() => {
      // Use user's boundary_id if available, otherwise use default (7)
      const boundaryId = user?.boundary_id || 7;

      // For admin users (is_staff), show all shehia; for regular users show only their locality
      if (user?.is_staff) {
        // Admin sees all shehia (type 23) from Django API with polygon data
        boundaryCRUD.loadBoundaryGeoJson(23).then((resp) => {
          setShehia(resp.data);
        });
        // Admin sees all zones (type 24)
        boundaryCRUD.loadBoundaryGeoJson(24).then((resp) => {
          setZone(resp.data);
        });
        // Admin sees all buildings
        buildingCRUD.loadBuildingJson().then((resp) => {
          setBuilding(resp.data);
        });
      } else if (user?.boundary_id) {
        // For regular users, load shehia for their boundary
        boundaryCRUD.loadBoundaryGeoJson(23, null).then((resp) => {
          // Filter to only show boundaries related to user
          setShehia(resp.data);
        }).catch(() => {
          setShehia([]);
        });
        // Load zones for the user's boundary
        boundaryCRUD.loadBoundaryGeoJson(24, boundaryId).then((resp) => {
          setZone(resp.data);
        });
        // Load buildings for the user's boundary
        buildingCRUD.loadSpecificBuildingJson(boundaryId).then((resp) => {
          setBuilding(resp.data);
        });
      }

      // Center map on user's boundary (or default for admin)
      boundaryCRUD.loadOneBoundaryMap(boundaryId).then((resp) => {
        if (resp.data[0]?.boundary_centroid !== null) {
          mapRef.current.flyTo(
            resp.data[0].boundary_centroid.coordinates.reverse(),
            16
          );
        }
      }).catch(() => {});

      // Load counts (these are already filtered by user's boundary in backend)
      kayaCRUD.loadKayaCount().then((resp) => {
        setKayaCount(resp.data);
      });
      kayaCRUD.loadKayaMemberCount().then((resp) => {
        setMemberCount(resp.data);
      });
      buildingCRUD.loadBuildingCountAll(25).then((resp) => {
        setBuildingRCount(resp.data);
      });
      buildingCRUD.loadBuildingCountAll(26).then((resp) => {
        setBuildingCCount(resp.data);
      });
      buildingCRUD.loadBuildingCountAll(27).then((resp) => {
        setBuildingCiCount(resp.data);
      });
      buildingCRUD.loadBuildingCountAll(28).then((resp) => {
        setBuildingReCount(resp.data);
      });
      buildingCRUD.loadBuildingCountAll(29).then((resp) => {
        setBuildingMCount(resp.data);
      });
      buildingCRUD.loadBuildingCountTotal().then((resp) => {
        setBuildingTotalCount(resp.data);
      });
      kayaCRUD.loadKayaMemberChart().then((resp) => {
        setYAxisMale(resp.data.result_male?.map((dt) => dt.total));
        setYAxisFemale(resp.data.result_female?.map((dt) => dt.total));
      });
      kayaCRUD.loadKayaMemberEducation().then((resp) => {
        // First 4 entries are the education levels (ids 54-57). Each entry is an
        // object with a single count value, so take Object.values(...)[0].
        const pick = (arr) =>
          (arr || []).slice(0, 4).map((o) => Object.values(o)[0] || 0);
        setEduMale(pick(resp.data.result_male));
        setEduFemale(pick(resp.data.result_female));
      });
    }, 500);
  }, [user]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.on("zoomend", function () {
        setZoom(mapRef.current._zoom);
      });
    }
  }, [mapRef.current]);

  const sFeature = (feature, layer) => {
    let popupContent = `
            <Popup>
                <p>${feature.properties.boundary_name}</p>
            </Popup>
        `;
    layer.bindPopup(popupContent);
    layer.on({
      mouseover: function () {
        layer.setStyle({
          weight: 3,
          color: "#ed071a",
          fillColor: "transparent",
          fillOpacity: 1,
        });
      },
      mouseout: function () {
        layer.setStyle({
          color: "#810541",
          weight: 3,
          fillColor: "transparent",
          fillOpacity: 1,
        });
      },
    });
  };

  const zFeature = (feature, layer) => {
    let popupContent = `
            <Popup>
                <p>${feature.properties.boundary_name}</p>
            </Popup>
        `;
    layer.bindPopup(popupContent);
    layer.on({
      mouseover: function () {
        layer.setStyle({
          weight: 1,
          color: "#ed071a",
          fillColor: "transparent",
          fillOpacity: 1,
        });
      },
      mouseout: function () {
        layer.setStyle({
          color: "#00008B",
          weight: 1,
          fillColor: "transparent",
          fillOpacity: 1,
        });
      },
    });
  };

  const bFeature = (feature, layer) => {
    let popupContent = null;
    switch (feature.properties.dictionary_building_category_id) {
      case 25:
        popupContent =
          '<div class="h5" style="height: 540px; width: 380px;">' +
          '<div class="row mb-3">' +
          '<div class="col-auto">' +
          '<img src="assets/demo/house-avatar.jpeg" class="img img-responsive img-thumbnail" width="300px"/>' +
          "</div>" +
          "</div>" +
          "<hr/>" +
          '<div class="row">' +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Household Name &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.name || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Postcode &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "71112" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Street &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "XXXX Banko Street" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "House Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.postcode || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Shehia Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          ("SH/BK/" + feature.properties.house_no || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Uses &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "Residential" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<div/>" +
          "</div>";
        break;
      case 26:
        popupContent =
          '<div class="h5" style="height: 540px; width: 380px;">' +
          '<div class="row mb-3">' +
          '<div class="col-auto">' +
          '<img src="assets/demo/house-avatar.jpeg" class="img img-responsive img-thumbnail" width="300px"/>' +
          "</div>" +
          "</div>" +
          "<hr/>" +
          '<div class="row">' +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Name &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.name || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Postcode &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "71112" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Street &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "XXXX Banko Street" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "House Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.postcode || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Shehia Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          ("SH/BK/" + feature.properties.house_no || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Uses &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto">' +
          "Commercila" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<div/>" +
          "</div>";
        break;
      case 27:
        popupContent =
          '<div class="h5" style="height: 540px; width: 380px;">' +
          '<div class="row mb-3">' +
          '<div class="col-auto">' +
          '<img src="assets/demo/house-avatar.jpeg" class="img img-responsive img-thumbnail" width="300px"/>' +
          "</div>" +
          "</div>" +
          "<hr/>" +
          '<div class="row">' +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Name &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.name || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Postcode &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "71112" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Street &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "XXXX Banko Street" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "House Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.postcode || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Shehia Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          ("SH/BK/" + feature.properties.house_no || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Uses &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto">' +
          "Civic" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<div/>" +
          "</div>";
        break;
      case 28:
        popupContent =
          '<div class="h5" style="height: 540px; width: 380px;">' +
          '<div class="row mb-3">' +
          '<div class="col-auto">' +
          '<img src="assets/demo/house-avatar.jpeg" class="img img-responsive img-thumbnail" width="300px"/>' +
          "</div>" +
          "</div>" +
          "<hr/>" +
          '<div class="row">' +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Name &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.name || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Postcode &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "71112" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Street &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "XXXX Banko Street" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "House Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.postcode || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Shehia Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          ("SH/BK/" + feature.properties.house_no || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Uses &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto">' +
          "Religious" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<div/>" +
          "</div>";
        break;
      case 29:
        popupContent =
          '<div class="h5" style="height: 540px; width: 380px;">' +
          '<div class="row mb-3">' +
          '<div class="col-auto">' +
          '<img src="assets/demo/house-avatar.jpeg" class="img img-responsive img-thumbnail" width="300px"/>' +
          "</div>" +
          "</div>" +
          "<hr/>" +
          '<div class="row">' +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Name &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.name || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Postcode &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "71112" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Street &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          "XXXX Banko Street" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "House Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          (feature.properties.postcode || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Shehia Number &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto mb-2">' +
          ("SH/BK/" + feature.properties.house_no || "N/A") +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building Uses &nbsp;:" +
          "</span></div>" +
          '<div class="col-auto">' +
          "Mixed use" +
          "</div>" +
          "<hr/>" +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "<div/>" +
          "</div>";
        break;
      default:
        popupContent =
          '<div class="h5" style="height: 30px; width: 250px;">' +
          '<div class="row">' +
          '<div class="col-auto"><span class="fw-bolder">' +
          "Building BRN &nbsp; :" +
          "</span></div>" +
          '<div class="col-auto">' +
          (feature.properties.brn || "N/A") +
          "</div>" +
          "</div>";
    }
    layer.bindPopup(popupContent);
    layer.on({
      mouseover: function () {
        layer.setStyle({
          weight: 0.9,
          color: "#ed071a",
          fillColor: "transparent",
          fillOpacity: 0.2,
        });
      },
      mouseout: function () {
        layer.setStyle({
          color: "#dddddd",
          weight: 1,
          fillColor: "transparent",
          fillOpacity: 0.2,
        });
      },
    });
  };

  let shehiaLayer = null;
  let zoneLayer = null;
  let buildingLayer = null;

  if (shehia.numberReturned !== undefined || shehia.numberReturned >= 1) {
    shehiaLayer = (
      <FeatureGroup>
        <GeoJSON
          data={shehia}
          style={() => ({
            color: "#810541",
            weight: 3,
            fillColor: "transparent",
            fillOpacity: 1,
          })}
          onEachFeature={sFeature}
        />
      </FeatureGroup>
    );
  }

  if (zone.numberReturned !== undefined || zone.numberReturned >= 1) {
    zoneLayer = (
      <FeatureGroup>
        <GeoJSON
          data={zone}
          style={() => ({
            color: "#00008B",
            weight: 1,
            fillColor: "transparent",
            fillOpacity: 1,
          })}
          onEachFeature={zFeature}
        />
      </FeatureGroup>
    );
  }

  if (building.numberReturned !== undefined || building.numberReturned >= 1) {
    buildingLayer = (
      <FeatureGroup>
        <GeoJSON
          data={building}
          style={() => ({
            color: "#dddddd",
            weight: 1,
            fillColor: "transparent",
            fillOpacity: 0.2,
          })}
          onEachFeature={bFeature}
        />
      </FeatureGroup>
    );
  }

  let chart = null;

  let chart2 = null;

  let chartEducation = null;

  if (yAxisMale) {
    // Grand total across all age groups (male + female) for percentage labels.
    const ageGrandTotal =
      (yAxisMale || []).reduce((a, b) => a + (b || 0), 0) +
      (yAxisFemale || []).reduce((a, b) => a + (b || 0), 0);

    const series = [
      {
        name: "Male Age's Range",
        data: yAxisMale,
      },
      {
        name: "Female Age's Range",
        data: yAxisFemale,
      },
    ];
    const options = {
      chart: { id: "bar-chart", background: "#F6F8FA" },
      xaxis: {
        categories: xAxis,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          endingShape: "rounded",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: {
        enabled: true,
        // Show "<count> (<percent>%)" of the total population above each bar.
        formatter: (val) => {
          if (!val) return "";
          const pct = ageGrandTotal
            ? ((val / ageGrandTotal) * 100).toFixed(1)
            : 0;
          return `${val} (${pct}%)`;
        },
        offsetY: -18,
        rotate: -90,
        style: { fontSize: "9px", colors: ["#304758"] },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            const pct = ageGrandTotal
              ? ((val / ageGrandTotal) * 100).toFixed(1)
              : 0;
            return `${val} (${pct}% of total)`;
          },
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
    };
    const options2 = {
      chart: { id: "pie", background: "#F6F8FA" },
      labels: xAxis,
    };
    const series1 = yAxisMale;

    // Education-level chart (Male/Female across the 4 education levels).
    const eduTotal =
      (eduMale || []).reduce((a, b) => a + (b || 0), 0) +
      (eduFemale || []).reduce((a, b) => a + (b || 0), 0);
    const eduSeries = [
      { name: "Male", data: eduMale },
      { name: "Female", data: eduFemale },
    ];
    const eduOptions = {
      chart: { id: "education-chart", background: "#F6F8FA" },
      colors: ["#0ab39c", "#f06548"],
      xaxis: { categories: eduLabels },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          if (!val) return "";
          const pct = eduTotal ? ((val / eduTotal) * 100).toFixed(1) : 0;
          return `${val} (${pct}%)`;
        },
        offsetY: -18,
        style: { fontSize: "10px", colors: ["#304758"] },
      },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      legend: { position: "top" },
    };

    chart = (
      <Chart
        options={options}
        series={series}
        type="bar"
        width="100%"
        height={450}
      />
    );
    chart2 = (
      <Chart
        options={options2}
        series={series1}
        type="pie"
        width="100%"
        height={450}
      />
    );
    chartEducation = (
      <Chart
        options={eduOptions}
        series={eduSeries}
        type="bar"
        width="100%"
        height={450}
      />
    );
  }

  return (
    <>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0">
                    Shehia Dashboard
                    {user?.admin_access && (
                      <span className={`badge ms-2 ${user.admin_access === 'Admin' ? 'bg-success' : 'bg-primary'}`}>
                        {user.admin_access === 'Admin' ? 'All Shehias' : user.area_display}
                      </span>
                    )}
                  </h4>

                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <a href="javascript: void(0);">Dashboards</a>
                      </li>
                      <li className="breadcrumb-item active">Analytics</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xxl-5">
                <div className="d-flex flex-column h-100">
                  <div className="row">
                    <div className="col-md-6">
                      <Link to={"/kaya"}>
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  House Hold
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <h2 className="mt-4 ff-secondary fw-semibold">
                                    <span
                                      className="counter-value"
                                      data-target={kayaCount}
                                    >
                                      {kayaCount}
                                    </span>
                                  </h2>
                                  {/* <span
                                    className="counter-value"
                                    data-target={kayaCount}
                                  >
                                    <NumericFormat
                                      value={kayaCount}
                                      displayType="text"
                                      thousandSeparator
                                      prefix=""
                                      renderText={(value) => (
                                        <p className="text-bold">{value}</p>
                                      )}
                                    />
                                  </span> */}
                                  {/* <p>{kayaCount}</p> */}
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-success mb-0">
                                    {" "}
                                    <i className="ri-arrow-up-line align-middle"></i>{" "}
                                    100 %{" "}
                                  </span>{" "}
                                  of All Household
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-success rounded-circle fs-2">
                                    <i className="text-success las la-campground"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>

                    <div className="col-md-6">
                      <Link to={"/member"}>
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  House Hold Member
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <span
                                    className="counter-value"
                                    data-target={memberCount}
                                  >{memberCount}
                                    {/* <NumericFormat
                                      value={memberCount}
                                      displayType="text"
                                      thousandSeparator
                                      prefix=""
                                      renderText={(value) => (
                                        <p className="text-bold">{value}</p>
                                      )}
                                    /> */}
                                  </span>
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-danger mb-0">
                                    {" "}
                                    <i className="ri-arrow-down-line align-middle"></i>{" "}
                                    100 %{" "}
                                  </span>{" "}
                                  of All Members
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-info rounded-circle fs-2">
                                    <i className="text-info las la-user"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Link to={"/building"}>
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  Total Buildings
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <span
                                    className="counter-value"
                                    data-target={buildingTotalCount}
                                  >
                                    {number.fShortenNumber(buildingTotalCount)}
                                  </span>
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-primary mb-0">
                                    {" "}
                                    100 %{" "}
                                  </span>{" "}
                                  of All Buildings
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-primary rounded-circle fs-2">
                                    <i className="text-primary las la-city"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>

                    <div className="col-md-6">
                      <Link
                        to={"/building"}
                        state={{ category: "Residential" }}
                      >
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  Residential Building
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <span
                                    className="counter-value"
                                    data-target={buildingRCount}
                                  >
                                    {number.fShortenNumber(buildingRCount)}
                                  </span>
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-success mb-0">
                                    {" "}
                                    {number.fPercent(
                                      (buildingRCount * 100) /
                                      (buildingRCount +
                                        buildingCCount +
                                        buildingCiCount +
                                        buildingReCount +
                                        buildingMCount)
                                    )}{" "}
                                  </span>{" "}
                                  of All buildings
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-primary rounded-circle fs-2">
                                    <i className="text-primary las la-home"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>

                    <div className="col-md-6">
                      <Link to={"/building"} state={{ category: "Commercial" }}>
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  Commercial Building
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <span
                                    className="counter-value"
                                    data-target={buildingCCount}
                                  >
                                    {number.fShortenNumber(buildingCCount)}
                                  </span>
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-danger mb-0">
                                    {" "}
                                    {number.fPercent(
                                      (buildingCCount * 100) /
                                      (buildingRCount +
                                        buildingCCount +
                                        buildingCiCount +
                                        buildingReCount +
                                        buildingMCount)
                                    )}{" "}
                                  </span>{" "}
                                  of All buildings
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-warning rounded-circle fs-2">
                                    <i className="text-warning las la-building"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Link to={"/building"} state={{ category: "Civic" }}>
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  Civic Building
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <span
                                    className="counter-value"
                                    data-target={buildingCiCount}
                                  >
                                    {number.fShortenNumber(buildingCiCount)}
                                  </span>
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-danger mb-0">
                                    {" "}
                                    {number.fPercent(
                                      (buildingCiCount * 100) /
                                      (buildingRCount +
                                        buildingCCount +
                                        buildingCiCount +
                                        buildingReCount +
                                        buildingMCount)
                                    )}{" "}
                                  </span>{" "}
                                  of All buildings
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-secondary rounded-circle fs-2">
                                    <i className="text-secondary las la-building"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>

                    <div className="col-md-6">
                      <Link to={"/building"} state={{ category: "Religious" }}>
                        <div className="card card-animate">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <p className="fw-medium text-muted mb-0">
                                  Religious Building
                                </p>
                                <h2 className="mt-4 ff-secondary fw-semibold">
                                  <span
                                    className="counter-value"
                                    data-target={buildingReCount}
                                  >
                                    {number.fShortenNumber(buildingReCount)}
                                  </span>
                                </h2>
                                <p className="mb-0 text-muted">
                                  <span className="badge bg-light text-success mb-0">
                                    {" "}
                                    {number.fPercent(
                                      (buildingReCount * 100) /
                                      (buildingRCount +
                                        buildingCCount +
                                        buildingCiCount +
                                        buildingReCount +
                                        buildingMCount)
                                    )}{" "}
                                  </span>{" "}
                                  of All buildings
                                </p>
                              </div>
                              <div>
                                <div className="avatar-sm flex-shrink-0">
                                  <span className="avatar-title bg-soft-dark rounded-circle fs-2">
                                    <i className="text-dark las la-star"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xxl-7">
                <div className="row h-100">
                  <div className="col-xl-12">
                    <div className="card card-height-100">
                      <div className="card-body p-1">
                        <div className="row">
                          <div className="col-xl-12">
                            <MapContainer
                              style={{
                                height: "50vh",
                                width: "100%",
                                borderRadius: 3,
                              }}
                              ref={mapRef}
                              whenCreated={(mapInstance) =>
                                (mapRef.current = mapInstance)
                              }
                              className="map"
                              scrollWheelZoom
                              center={[-6.1542, 39.3613]}
                              zoom={10}
                              minZoom={5}
                              maxZoom={18}
                              zoomControl={true}
                            >
                              <LayersControl>
                                <BaseLayer name="OpenMap">
                                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}.png" />
                                </BaseLayer>
                                <BaseLayer name="StreetMap">
                                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                </BaseLayer>
                                <BaseLayer name="OSM">
                                  <TileLayer url="https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}.png" />
                                </BaseLayer>
                                <BaseLayer name="RoadMap">
                                  <TileLayer url="https://mt0.google.com/vt/lyrs=r&hl=en&x={x}&y={y}&z={z}" />
                                </BaseLayer>
                                <BaseLayer checked name="HybridMap">
                                  <TileLayer url="https://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}" />
                                </BaseLayer>
                              </LayersControl>
                              <FeatureGroup>
                                {zoom >= 17 && buildingLayer}
                              </FeatureGroup>
                              <FeatureGroup>{zoneLayer}</FeatureGroup>
                              <FeatureGroup>{shehiaLayer}</FeatureGroup>
                            </MapContainer>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xxl-6">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title" id="myLargeModalLabel">
                      Age's Range (Male / Female with %)
                    </h5>
                  </div>
                  <div className="card-body p-0">{chart}</div>
                </div>
              </div>
              <div className="col-xxl-6">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title" id="myLargeModalLabel">
                      Age's Range (Distribution)
                    </h5>
                  </div>
                  <div className="card-body p-0">{chart2}</div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title" id="myLargeModalLabel">
                      Education Level
                    </h5>
                  </div>
                  <div className="card-body p-0">{chartEducation}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};
